import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import useEvents from '../../../hooks/useEvents';
import { EVENT_TYPES } from '../../../shared/constants/events';
import GROWTHBOOK_FEATURE_FLAGS from '../../../shared/utils/growthbook.util';
import useFeaturesReady from '../../../shared/featureFlags/useFeaturesReady';
import { logger } from '../../../shared/utils/logger';
import { useSATConnection } from '../useSATConnection';
import { assignLinkingReward } from './linkingReward.service';
import {
	canAssign,
	NO_REWARD,
	resolveArm,
	toRewardState,
	type LinkingRewardState,
} from './resolveLinkingReward';

/**
 * Experimento de 2 brazos (control / $100) sobre el premio en heru-cash por
 * vincular el SAT. El VALOR de la feature ES el monto, así que el brazo se lee
 * de una: `value` → cuánto, `experimentResult.variationId` → cuál.
 *
 * Nada aquí depende de CUÁNTOS brazos haya: `resolveArm` sólo exige que el valor
 * sea un número y que venga con experimentResult. Pasar de 3 brazos a 2 no tocó
 * una línea de esta lógica, sólo la clave de la feature.
 */
export const LINKING_REWARD_FLAG =
	GROWTHBOOK_FEATURE_FLAGS.LINKING_REWARD_AMOUNT;

/** Lo que el hook devuelve: el estado de la promesa MÁS el reporte de que el
 * usuario abrió el explicador de heru-cash. El reporte vive aquí —y no en cada
 * superficie— porque las dos del flujo legacy (LinkingRewardPromise y
 * LinkingFlow) ya consumen este hook: es el único punto donde el nombre del
 * evento y su payload no pueden divergir entre ellas. */
export type LinkingRewardSurface = LinkingRewardState & {
	onExplainerOpen: () => void;
};

/**
 * Resuelve el brazo del usuario y registra la promesa en el backend.
 *
 * # Por qué el front resuelve GrowthBook y no el backend
 *
 * No hay SDK de GrowthBook en Go. Si el backend resolviera, el
 * `trackingCallback` del SDK nunca dispararía y NO HABRÍA EVENTO DE EXPOSICIÓN
 * — o sea, no habría experimento que analizar. El backend valida que el brazo
 * que le mandamos sea uno de los configurados, lo congela por user_id y es
 * idempotente.
 *
 * # Qué dispara la exposición
 *
 * `evalFeature`. El SDK corre el experimento ahí dentro y de ahí sale el
 * `trackingCallback` (deduplicado por experimento), incluido el brazo de
 * CONTROL — que es justo lo que hace que el control tenga denominador. Por eso
 * la llamada vive en un efecto y no en el render: la exposición debe quedar
 * registrada cuando el usuario de verdad abre el flujo, no como efecto
 * colateral de un render que React puede descartar.
 */
export const useLinkingReward = (): LinkingRewardSurface => {
	const featuresReady = useFeaturesReady();
	const growthbook = useGrowthBook();
	// heru-cash sólo se muestra —y sólo se puede gastar en el checkout— para la
	// cohorte de este flag. Prometer créditos fuera de ella sería prometer algo
	// que el usuario no puede ver ni redimir, y además envenenaría la métrica
	// primaria: el revenue neto del brazo tratado se leería como si el crédito
	// nunca se hubiera usado. Gatear aquí deja además el denominador limpio,
	// porque la exposición no se dispara para quien no podría cobrar.
	// Ya vinculado = no hay premio posible (el otorgamiento exige IsNewLink).
	// Se lee aquí, en el hook, para que TODAS las superficies queden protegidas:
	// las de vinculación son mixtas y no pueden decidirlo solas.
	const { isLinked, isRelinking } = useSATConnection();
	const heruCashEnabled = useFeatureIsOn(
		GROWTHBOOK_FEATURE_FLAGS.REFERRALS_SERVICE,
	);
	const { trackEvent } = useEvents();
	// SE LEE EL ATRIBUTO REAL DE GROWTHBOOK, NO EL PERFIL DE REDUX.
	//
	// `user_id` es el atributo de hasheo del experimento y no existe al arrancar.
	// GrowthBook descarta del experimento a quien lo tenga vacío, así que evaluar
	// antes devuelve el default SIN experimentResult y el usuario se queda sin
	// promesa — y no se ve como error, se ve como alguien que no cayó en el
	// experimento. Sesgo silencioso.
	//
	// Mirar el perfil de Redux NO alcanza: sólo `Protected.routes` despacha
	// setProfile Y llama updateAttributes juntos. Los otros caminos de login
	// (Verify.tsx, usePostAuthRedirect) despachan el perfil sin tocar GrowthBook,
	// y la query de Protected.routes está gateada con `!userProfile`, así que con
	// el perfil ya en Redux nunca corre. Un usuario que entra por deeplink a
	// /declaraciones tras el OTP tendría perfil en Redux y GrowthBook sin
	// user_id: el sesgo quedaría correlacionado con el camino de entrada.
	//
	// Leerlo en render y no dentro del efecto es lo que lo hace reactivo: el
	// provider registra un renderer con el SDK (growthbook-react index.js:131),
	// así que updateAttributes fuerza re-render, este valor cambia y el efecto
	// vuelve a correr cuando el atributo por fin aterriza.
	const hashAttribute = growthbook?.getAttributes()?.user_id;

	const [state, setState] = useState<LinkingRewardState>(NO_REWARD);
	const requested = useRef(false);

	useEffect(() => {
		if (
			!growthbook ||
			!canAssign({
				featuresReady,
				hasGrowthbook: true,
				heruCashEnabled,
				hashAttribute,
				alreadyLinked: isLinked || Boolean(isRelinking),
			})
		) {
			return;
		}
		// Una sola asignación por montaje del flujo. El endpoint es idempotente,
		// así que un disparo de más no reasignaría ni cobraría nada — pero
		// tampoco hay razón para hacerlo.
		if (requested.current) return;

		const arm = resolveArm(growthbook.evalFeature<number>(LINKING_REWARD_FLAG));
		// El intento NO se marca como consumido hasta aquí. Marcarlo antes de
		// tener brazo era el bug: un montaje temprano quemaba el único intento y
		// el usuario perdía su promesa aunque el atributo aterrizara un segundo
		// después.
		if (!arm) return;
		requested.current = true;

		assignLinkingReward({ experimentId: LINKING_REWARD_FLAG, ...arm })
			.then((assignment) => setState(toRewardState(assignment)))
			.catch((error) => {
				// Se falla CERRADO: sin promesa registrada, no se pinta el banner.
				// Anunciar un premio que el backend no reconoce es fabricar una queja
				// de soporte — el otorgamiento lee esa fila, no el banner.
				//
				// Pero el intento SE LIBERA. Para cuando llegamos aquí la exposición
				// ya se disparó dentro de evalFeature, así que el usuario ya cuenta
				// en el denominador de su brazo: dejarlo quemado por un 500
				// transitorio lo deja expuesto y sin promesa, diluyendo el efecto
				// medido. El endpoint es idempotente, así que reintentar es barato.
				requested.current = false;
				trackEvent({
					eventName: 'linking_reward_assign_failed',
					eventPayload: {
						experiment_id: LINKING_REWARD_FLAG,
						variation_id: arm.variationId,
						reward_amount: arm.rewardAmount,
						status: error?.response?.status ?? null,
					},
				});
				// logger.error es no-op fuera de DEV, por eso arriba va un evento:
				// sin él, "expuestos sin promesa" sería invisible en producción.
				logger.error('No se pudo registrar la promesa del premio', error);
			});
	}, [featuresReady, growthbook, heruCashEnabled, hashAttribute, trackEvent]);

	const onExplainerOpen = useCallback(() => {
		trackEvent({
			eventName: EVENT_TYPES.HERU_CASH_EXPLAINER_VIEWED,
			eventPayload: {
				source: 'sat_linking_legacy',
				reward_amount: state.rewardAmount,
			},
		});
	}, [state.rewardAmount, trackEvent]);

	return { ...state, onExplainerOpen };
};

export default useLinkingReward;
